<template>
  <div class="kb-rows">
    <!-- Approval: for bands this is per parade year -->
    <div class="kb-row">
      <div>
        <h4
          id="subkrewe-approval-label"
          class="kb-row-title"
        >
          {{ isYearlyApproval ? `${paradeYear} parade approval` : 'Approval' }}
        </h4>
        <p
          v-if="isYearlyApproval"
          class="kb-row-hint"
        >
          Bands are approved for each parade separately
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdating"
          label="Saving"
        />
        <KbChoice
          v-model="approvalValue"
          :options="approvalOptions"
          :disabled="isUpdating"
          aria-labelledby="subkrewe-approval-label"
          @change="updateApproval"
        />
      </div>
    </div>

    <!-- Sponsored -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Sponsored by Chewbacchus
        </h4>
        <p class="kb-row-hint">
          Members don't pay dues. Turning this on sets admission to closed.
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingSponsored"
          label="Saving"
        />
        <ToggleSwitch
          v-model="sponsoredValue"
          :disabled="isUpdatingSponsored"
          aria-label="Sponsored by Chewbacchus"
          @change="updateSponsored"
        />
      </div>
    </div>

    <!-- Specialty flags (admin only) -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Specialties
        </h4>
        <p class="kb-row-hint">
          Internal roles like free-range red shirts or nerd herders
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingSpecialty"
          label="Saving"
        />
        <MultiSelect
          v-model="specialtyValue"
          :options="specialtyOptions"
          option-label="label"
          option-value="value"
          :disabled="!isAdmin || isUpdatingSpecialty"
          class="w-48"
          size="small"
          :show-toggle-all="false"
          placeholder="None"
          aria-label="Specialties"
          @change="updateSpecialty"
        />
      </div>

      <!-- Nerd herder cap, only when the nerd herder specialty is on -->
      <div
        v-if="specialtyValue?.includes('redshirtNerdHerders')"
        class="kb-row-body"
      >
        <div class="flex items-center justify-between gap-4">
          <div>
            <label
              for="nerdHerderCap"
              class="text-sm font-semibold text-kb-fg"
            >Nerd herder cap</label>
            <p class="kb-row-hint">
              Most nerd herders allowed; 0 or empty means no cap
            </p>
          </div>
          <div class="flex items-center gap-2">
            <KbSpinner
              v-if="isUpdatingNerdHerderCap"
              label="Saving"
            />
            <InputText
              id="nerdHerderCap"
              v-model="nerdHerderCapValue"
              type="number"
              size="small"
              class="w-20"
              placeholder="25"
              :disabled="!isAdmin || isUpdatingNerdHerderCap"
              min="0"
              @blur="saveNerdHerderCap"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Contraptions -->
    <div
      v-if="props.subkrewe.contraption"
      class="kb-row"
    >
      <div class="min-w-0">
        <h4 class="kb-row-title">
          Contraptions
        </h4>
        <p class="kb-row-hint">
          {{ props.subkrewe.contraption_info || 'No details provided' }}
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingContraptionCount"
          label="Saving"
        />
        <label
          for="contraptionCount"
          class="kb-row-meta"
        >count</label>
        <InputText
          id="contraptionCount"
          v-model="contraptionCountValue"
          type="number"
          size="small"
          class="w-16"
          min="1"
          :disabled="isUpdatingContraptionCount"
          @blur="saveContraptionCount"
        />
      </div>
    </div>

    <!-- Parking passes bought by this SubKrewe's members -->
    <div
      v-if="props.subkrewe.contraption"
      class="kb-row"
    >
      <div class="min-w-0">
        <h4 class="kb-row-title">
          Parking passes
        </h4>
        <p
          v-if="totalParkingPasses === 0"
          class="kb-row-hint"
        >
          None bought yet
        </p>
        <ul
          v-else
          class="kb-row-meta m-0 mt-0.5 p-0 list-none"
        >
          <li
            v-for="member in parkingTickets"
            :key="member.id"
          >
            <i
              v-if="member.isCaptain"
              class="pi pi-star-fill !text-[9px] mr-1 text-[color:var(--kb-type-captain)]"
            />{{ member.name }} · {{ member.passCount }} {{ member.passCount > 1 ? 'passes' : 'pass' }}
          </li>
        </ul>
      </div>
      <div class="kb-row-aside">
        <span
          v-if="totalParkingPasses > 0"
          class="kb-tag kb-tag-ok"
        >{{ totalParkingPasses }} {{ totalParkingPasses > 1 ? 'passes' : 'pass' }}</span>
      </div>
    </div>

    <!-- Delete -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Delete SubKrewe
        </h4>
        <p class="kb-row-hint">
          Removes every member from it; can't be undone
        </p>
      </div>
      <div class="kb-row-aside">
        <Button
          label="Delete SubKrewe"
          icon="pi pi-trash"
          outlined
          severity="danger"
          size="small"
          :disabled="isDeleting"
          @click="confirmDelete"
        />
      </div>
    </div>

    <!-- Delete confirmation -->
    <Dialog
      v-model:visible="deleteDialogVisible"
      header="Delete SubKrewe"
      modal
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You're about to delete <strong>{{ props.subkrewe.name }}</strong>.
        </p>
        <p class="kb-dialog-warning">
          Every member is removed from the SubKrewe. This can't be undone.
        </p>
        <div class="kb-field">
          <label for="deleteReason">Reason <span class="kb-field-optional">(optional)</span></label>
          <InputText
            id="deleteReason"
            v-model="deleteReason"
            size="small"
            fluid
            autofocus
            placeholder="Why it's being deleted"
          />
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="deleteDialogVisible = false"
        />
        <Button
          label="Delete SubKrewe"
          severity="danger"
          :loading="isDeleting"
          @click="deleteSubKrewe"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { Member } from '~/shared/types/member'
import type { SubKrewe, SubKreweSpecialty } from '~/shared/types/subkrewe'
import { ADMISSION_POLICIES } from '~/shared/utils/admissionPolicies'
import { getParadeApprovalStatus, isPerYearApprovalSubkrewe } from '~/shared/utils/subkreweApproval'
import type { ApprovalStatus } from '~/shared/utils/subkreweApproval'

interface Props {
  subkrewe: SubKrewe
}

const props = defineProps<Props>()
const emit = defineEmits(['update', 'delete'])

const subkrewesStore = useSubKrewesStore()
const toast = useToast()
const isUpdating = ref(false)
const { paradeYear } = useParadeYear()

// Bands are decided per parade year; everything else once.
const isYearlyApproval = computed(() => isPerYearApprovalSubkrewe(props.subkrewe))
const currentApproval = computed(() => getParadeApprovalStatus(props.subkrewe, paradeYear.value))
const approvalValue = ref<ApprovalStatus>(currentApproval.value)

// Delete SubKrewe state
const deleteDialogVisible = ref(false)
const deleteReason = ref('')
const isDeleting = ref(false)

// Sponsored toggle state
const sponsoredValue = ref(props.subkrewe.sponsored || false)
const isUpdatingSponsored = ref(false)

// Specialty flags state
const specialtyOptions = [
  { label: 'Free-Range Red Shirts', value: 'redshirtFreeFloating' },
  { label: 'Nerd Herders', value: 'redshirtNerdHerders' },
  { label: 'Support (non-rolling)', value: 'support' },
  { label: 'Internal', value: 'internal' },
]
const specialtyValue = ref<SubKreweSpecialty[]>(props.subkrewe.specialty || [])
const isUpdatingSpecialty = ref(false)
const nerdHerderCapValue = ref<string>(
  typeof props.subkrewe.nerd_herder_cap === 'number' ? String(props.subkrewe.nerd_herder_cap) : '',
)
const isUpdatingNerdHerderCap = ref(false)

// Contraption count state
const contraptionCountValue = ref<string>(
  typeof props.subkrewe.contraption_count === 'number' ? String(props.subkrewe.contraption_count) : '1',
)
const isUpdatingContraptionCount = ref(false)

// Remove this line:
// const currentMember = await useCurrentMember()
// const isAdmin = computed(() => Boolean(currentMember?.roles?.admin))

const currentMember = ref<Member | null>(null)
const isAdmin = computed(() => Boolean(currentMember.value && currentMember.value.roles?.admin))

onMounted(async () => {
  currentMember.value = await useCurrentMember()
})

// Check which captains have purchased parking (only captains can buy parking)
const parkingTickets = computed(() => {
  const result: Array<{ id: string, name: string, passCount: number, isCaptain: boolean }> = []

  // Check captains only - they are the only ones who can purchase parking
  if (props.subkrewe.captains && props.subkrewe.captains.length > 0) {
    props.subkrewe.captains.forEach((captain) => {
      const parking = captain.purchases?.[paradeYear.value]?.parking
      if (parking && Array.isArray(parking) && parking.length > 0) {
        const passCount = parking.filter(p => p.status === 'completed').length
        if (passCount > 0) {
          result.push({
            id: captain.id,
            name: captain.name,
            passCount,
            isCaptain: true,
          })
        }
      }
    })
  }

  return result
})

// Total parking passes count
const totalParkingPasses = computed(() => {
  return parkingTickets.value.reduce((sum, m) => sum + m.passCount, 0)
})

// Show delete confirmation dialog
const confirmDelete = () => {
  deleteDialogVisible.value = true
}

// Delete the SubKrewe
const deleteSubKrewe = async () => {
  isDeleting.value = true
  const user = useCurrentUser()

  try {
    const success = await subkrewesStore.deleteSubKrewe(
      props.subkrewe.id,
      user.value?.uid || '',
      deleteReason.value,
    )

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'SubKrewe Deleted',
        detail: `${props.subkrewe.name} has been successfully deleted`,
        life: 3000,
      })

      deleteDialogVisible.value = false
      emit('delete', props.subkrewe.id)
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete SubKrewe',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isDeleting.value = false
  }
}

// Update approval status
const updateApproval = async () => {
  if (isUpdating.value) return // Prevent multiple submissions

  isUpdating.value = true

  try {
    // Look up a pending SubKreweApprovalTask for this subkrewe so the status
    // change flows through the same pipeline as the admin task list. This
    // keeps task.status in sync with subkrewes/{id}.approved and stamps the
    // approval audit fields. Falls back to a direct write only when no task
    // exists (e.g. legacy auto-approved subkrewes).
    const tasksStore = useTasksStore()
    const pendingTask = tasksStore.tasksArray.find(t =>
      t.taskImplementation === 'SubKreweApprovalTask'
      && t.metadata?.subkreweId === props.subkrewe.id
      && t.status === 'pending',
    )

    let success = false
    if (pendingTask && (approvalValue.value === 'Yes' || approvalValue.value === 'No')) {
      const reason = approvalValue.value === 'No' ? 'Denied by administrator' : undefined
      success = await tasksStore.processSubKreweApprovalTask(
        pendingTask.id,
        approvalValue.value === 'Yes',
        currentMember.value?.id,
        reason,
      )
    }
    else {
      success = await subkrewesStore.setApproval(props.subkrewe, approvalValue.value, {
        scope: 'parade',
        actorId: currentMember.value?.id,
        reason: approvalValue.value === 'No' ? 'Denied by administrator' : null,
      })
    }

    if (success) {
      // Force update parent SubKrewe component
      emit('update', isYearlyApproval.value ? {} : { approved: approvalValue.value })

      // No need to manually subscribe - useCoreSubscriptions handles this
      // Just fetch the latest data to ensure UI consistency
      await subkrewesStore.fetchSubKrewe(props.subkrewe.id)

      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: isYearlyApproval.value
          ? `${paradeYear.value} parade approval set to ${approvalValue.value}`
          : `SubKrewe approval status set to ${approvalValue.value}`,
        life: 3000,
      })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update approval status',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
  }
}

// Update sponsored status
const updateSponsored = async () => {
  if (isUpdatingSponsored.value) return // Prevent multiple submissions

  isUpdatingSponsored.value = true

  try {
    const updateData: Partial<SubKrewe> = {
      sponsored: sponsoredValue.value,
    }

    // If setting to sponsored, also set admission policy to CLOSED
    if (sponsoredValue.value === true) {
      updateData.admission_policy = ADMISSION_POLICIES.CLOSED
    }

    const success = await subkrewesStore.updateSubKrewe(
      props.subkrewe,
      updateData,
    )

    if (success) {
      emit('update', {
        sponsored: sponsoredValue.value,
        ...(sponsoredValue.value === true ? { admission_policy: ADMISSION_POLICIES.CLOSED } : {}),
      })

      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: `SubKrewe sponsorship status set to ${sponsoredValue.value ? 'Sponsored' : 'Not Sponsored'}`,
        life: 3000,
      })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update sponsorship status',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingSponsored.value = false
  }
}

// Update specialty flags
const updateSpecialty = async () => {
  if (isUpdatingSpecialty.value) return
  isUpdatingSpecialty.value = true
  try {
    const updateData: Partial<SubKrewe> = {
      specialty: specialtyValue.value,
    }
    // If nerd herder specialty removed, clear cap
    if (!specialtyValue.value?.includes('redshirtNerdHerders')) {
      updateData.nerd_herder_cap = undefined
      nerdHerderCapValue.value = ''
    }
    const success = await subkrewesStore.updateSubKrewe(props.subkrewe, updateData)
    if (success) {
      emit('update', { specialty: specialtyValue.value })
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: 'Specialty flags updated',
        life: 3000,
      })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update specialty flags',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingSpecialty.value = false
  }
}

// Save nerd herder cap on blur
const saveNerdHerderCap = async () => {
  if (isUpdatingNerdHerderCap.value) return
  if (!specialtyValue.value?.includes('redshirtNerdHerders')) return

  isUpdatingNerdHerderCap.value = true
  try {
    // Normalize input string -> integer >= 0, undefined to remove
    const parsed = parseInt(nerdHerderCapValue.value, 10)
    const value: number | undefined = Number.isFinite(parsed) && !Number.isNaN(parsed)
      ? Math.max(0, Math.floor(parsed))
      : undefined

    const success = await subkrewesStore.updateSubKrewe(props.subkrewe, { nerd_herder_cap: value })
    if (success) {
      emit('update', { nerd_herder_cap: value })
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: 'Nerd herder cap saved',
        life: 2000,
      })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save nerd herder cap',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingNerdHerderCap.value = false
  }
}

// Save contraption count on blur
const saveContraptionCount = async () => {
  if (isUpdatingContraptionCount.value) return

  isUpdatingContraptionCount.value = true
  try {
    const parsed = parseInt(contraptionCountValue.value, 10)
    const value: number = Number.isFinite(parsed) && !Number.isNaN(parsed)
      ? Math.max(1, Math.floor(parsed))
      : 1

    const success = await subkrewesStore.updateSubKrewe(props.subkrewe, { contraption_count: value })
    if (success) {
      emit('update', { contraption_count: value })
      toast.add({
        severity: 'success',
        summary: 'Updated',
        detail: 'Contraption count saved',
        life: 2000,
      })
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save contraption count',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingContraptionCount.value = false
  }
}

// Watch for external changes to subkrewe
watch(currentApproval, (newValue) => {
  if (newValue !== approvalValue.value) {
    approvalValue.value = newValue
  }
})

watch(() => props.subkrewe.sponsored, (newValue) => {
  if (newValue !== sponsoredValue.value) {
    sponsoredValue.value = newValue || false
  }
})

watch(() => props.subkrewe.specialty, (newValue) => {
  if (JSON.stringify(newValue) !== JSON.stringify(specialtyValue.value)) {
    specialtyValue.value = newValue || []
  }
})

watch(() => props.subkrewe.contraption_count, (newValue) => {
  const newStr = typeof newValue === 'number' ? String(newValue) : '1'
  if (newStr !== contraptionCountValue.value) {
    contraptionCountValue.value = newStr
  }
})

// Approval: a segmented choice whose chosen option shows the state's color
const approvalOptions: { label: string, value: ApprovalStatus, tone: 'ok' | 'warn' | 'alert' }[] = [
  { label: 'Approved', value: 'Yes', tone: 'ok' },
  { label: 'Pending', value: 'Pending', tone: 'warn' },
  { label: 'Denied', value: 'No', tone: 'alert' },
]
</script>
